<template>
  <div
    class="chart-wrapper"
    :style="{ minHeight: computedHeight }"
  >
    <!-- Toolbar -->
    <div
      v-if="downloadable && hasChartData"
      class="chart-toolbar"
    >
      <v-tooltip
        text="Diagramm als PNG herunterladen"
        location="bottom"
        color="surface" 
        content-class="tooltip"
      >
        <template #activator="{ props: tooltipProps }">
          <v-btn
            v-bind="tooltipProps"
            icon
            density="comfortable"
            variant="tonal"
            color="primary"
            class="chart-download-button"
            aria-label="Diagramm als PNG herunterladen"
            @click="downloadImage"
          >
            <v-icon>mdi-download-outline</v-icon>
          </v-btn>
        </template>
      </v-tooltip>
    </div>

    <!-- Empty state -->
    <div
      v-if="!hasChartData"
      class="chart-empty-state"
      role="status"
    >
      <div class="chart-empty-icon">
        <v-icon size="36" color="primary">
          mdi-chart-bar-stacked
        </v-icon>
      </div>

      <div class="text-subtitle-1 font-weight-bold">
        Keine Diagrammdaten vorhanden
      </div>

      <div class="text-body-2 text-medium-emphasis mt-1">
        Für die aktuelle Auswahl stehen keine auswertbaren Werte zur Verfügung.
      </div>
    </div>

    <!-- Chart -->
    <div
      v-else
      class="chart-canvas-container"
      :style="{ height: computedHeight }"
      :aria-label="ariaLabel"
      role="img"
    >
      <Bar
        ref="chartRef"
        :data="safeChartData"
        :options="mergedOptions"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { Bar } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { normalizeChartOptions } from './chartOptions'

const FancyBarShadow = {
  id: 'fancyBarShadow',

  beforeDatasetDraw(chart, args, pluginOptions) {
    const { ctx } = chart

    const {
      blur = 8,
      color = 'rgba(0, 0, 0, 0.15)',
      offsetX = 0,
      offsetY = 4,
    } = pluginOptions || {}

    ctx.save()
    ctx.shadowColor = color
    ctx.shadowBlur = blur
    ctx.shadowOffsetX = offsetX
    ctx.shadowOffsetY = offsetY
  },

  afterDatasetDraw(chart) {
    chart.ctx.restore()
  },
}

const safeChartData = computed(() => {
  const labels = Array.isArray(props.chartData?.labels)
    ? props.chartData.labels
    : []

  const datasets = Array.isArray(props.chartData?.datasets)
    ? props.chartData.datasets
        .filter(dataset => dataset && typeof dataset === 'object')
        .map(dataset => ({
          ...dataset,
          data: Array.isArray(dataset.data)
            ? dataset.data
            : [],
        }))
    : []

  return {
    labels,
    datasets,
  }
})

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  ChartDataLabels,
  FancyBarShadow
)

const props = defineProps({
  chartData: {
    type: Object,
    required: true,
  },

  options: {
    type: Object,
    required: true,
  },

  height: {
    type: [Number, String],
    default: 360,
  },

  ariaLabel: {
    type: String,
    default: 'Balkendiagramm',
  },

  downloadable: {
    type: Boolean,
    default: false,
  },

  filename: {
    type: String,
    default: '',
  },

  /** Context baked into the downloaded PNG's caption band and filename */
  projectName: { type: String, default: '' },
  methodName: { type: String, default: '' },
})

const chartRef = ref(null)

/* Alt-Optionen (Chart.js 2) nach plugins.* heben — siehe LineChart.vue. */
const normalizedOptions = computed(() => normalizeChartOptions(props.options))

const computedHeight = computed(() => {
  return typeof props.height === 'number'
    ? `${props.height}px`
    : props.height
})

const hasChartData = computed(() => {
  return Boolean(
    safeChartData.value.labels.length > 0 &&
    safeChartData.value.datasets.some(dataset => {
      return dataset.data.length > 0
    })
  )
})

const mergedOptions = computed(() => {
  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 150,

    animation: {
      duration: 300,
      easing: 'easeOutQuart',
    },

    plugins: {
      fancyBarShadow: {
        blur: 8,
        color: 'rgba(0, 0, 0, 0.15)',
        offsetX: 0,
        offsetY: 4,
      },

      datalabels: {
        display: hasChartData.value,
      },
    },
  }

  return {
    ...baseOptions,
    ...normalizedOptions.value,

    plugins: {
      ...baseOptions.plugins,
      ...(normalizedOptions.value?.plugins || {}),

      datalabels: {
        ...baseOptions.plugins.datalabels,
        ...(normalizedOptions.value?.plugins?.datalabels || {}),

        /*
         * Always disable the plugin when there is no usable data.
         */
        display: hasChartData.value
          ? (
              normalizedOptions.value?.plugins?.datalabels?.display ??
              true
            )
          : false,
      },
    },
  }
})

function downloadImage() {
  // Get Chart.js instance via vue-chartjs <Bar> ref
  const chart = chartRef.value?.chart
  if (!chart) return
  const url = chart.toBase64Image()
  const link = document.createElement('a')
  link.href = url
  link.download = 'chart.png'
  link.click()
}
</script>

<style scoped lang="scss">
.chart-wrapper {
  position: relative;
  width: 100%;
}

.chart-canvas-container {
  position: relative;
  width: 100%;
  min-width: 0;
}

.chart-toolbar {
  position: absolute;
  top: 0.65rem;
  right: 0.65rem;
  z-index: 5;
}

.chart-download-button {
  border-radius: 999px;
  backdrop-filter: saturate(140%) blur(4px);
}

.chart-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  min-height: 340px;
  padding: 2rem 1rem;

  text-align: center;
}

.chart-empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 64px;
  height: 64px;
  margin-bottom: 1rem;

  border-radius: 999px;

  background-color: rgba(var(--v-theme-primary), 0.08);
}

:deep(canvas) {
  max-width: 100%;
  border-radius: 12px;
}
</style>